import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, SparklesIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import Badge from '../common/Badge';
import betslipService from '../../services/betslipService';

/**
 * BetSlipDetailModal - Shows detailed betslip info with Knewton analysis
 */
export default function BetSlipDetailModal({ betslip, isOpen, onClose }) {
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  // Load analysis when modal opens
  useEffect(() => {
    let isMounted = true;
    let blobUrl = null;

    const loadData = async () => {
      if (!isOpen || !betslip?.id) return;

      // Load analysis
      setAnalyzing(true);
      try {
        const result = await betslipService.analyzeBetslip(betslip.id);
        if (isMounted) {
          setAnalysis(result);
        }
      } catch (error) {
        console.error('Error loading analysis:', error);
      } finally {
        if (isMounted) {
          setAnalyzing(false);
        }
      }

      // Load image
      if (betslip?.image_url) {
        try {
          const url = await betslipService.fetchImage(betslip.id);
          blobUrl = url;
          if (isMounted) {
            setImageUrl(url);
          }
        } catch (error) {
          console.error('Error loading image:', error);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [isOpen, betslip?.id, betslip?.image_url]);

  const handleClose = useCallback(() => {
    setAnalysis(null);
    setImageUrl(null);
    onClose();
  }, [onClose]);

  if (!isOpen || !betslip) return null;

  // Status badge configuration
  const statusConfig = {
    pending: { variant: 'warning', label: 'Pending' },
    won: { variant: 'success', label: 'Won' },
    lost: { variant: 'error', label: 'Lost' },
    pushed: { variant: 'info', label: 'Push' },
    cancelled: { variant: 'default', label: 'Cancelled' },
  };

  const status = statusConfig[betslip.status] || statusConfig.pending;

  // Format date
  const formattedDate = new Date(betslip.bet_date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-secondary rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-text-primary">
              {betslip.betslip_type === 'parlay'
                ? `${betslip.legs?.length || 0}-Leg Parlay`
                : betslip.betslip_type === 'same_game_parlay' || betslip.betslip_type === 'same_game_parlay_plus'
                ? 'Same Game Parlay'
                : 'Single Bet'}
            </h2>
            <Badge variant={status.variant} size="sm">
              {status.label}
            </Badge>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-bg-card rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {/* Bet Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-bg-card p-3 rounded-lg">
              <p className="text-xs text-text-secondary mb-1">Sportsbook</p>
              <p className="text-sm font-semibold text-text-primary">{betslip.sportsbook}</p>
            </div>
            <div className="bg-bg-card p-3 rounded-lg">
              <p className="text-xs text-text-secondary mb-1">Date</p>
              <p className="text-sm font-semibold text-text-primary">{formattedDate}</p>
            </div>
            <div className="bg-bg-card p-3 rounded-lg">
              <p className="text-xs text-text-secondary mb-1">Stake</p>
              <p className="text-sm font-semibold text-text-primary">
                ${betslip.stake_amount?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className="bg-bg-card p-3 rounded-lg">
              <p className="text-xs text-text-secondary mb-1">Potential Win</p>
              <p className="text-sm font-semibold text-accent-green">
                ${betslip.potential_payout?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>

          {/* Knewton Analysis Section */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="bg-bg-card p-3 border-b border-border flex items-center gap-2">
              <SparklesIcon className="w-5 h-5 text-accent-blue" />
              <h3 className="font-semibold text-text-primary">Knewton Analysis</h3>
            </div>

            {analyzing ? (
              <div className="p-6 flex items-center justify-center gap-3">
                <div className="animate-spin h-5 w-5 border-2 border-accent-blue border-t-transparent rounded-full"></div>
                <span className="text-text-secondary">Analyzing your picks...</span>
              </div>
            ) : analysis ? (
              <div className="p-4 space-y-4">
                {/* Summary */}
                <div className={`p-3 rounded-lg border ${
                  analysis.summary?.avg_confidence >= 60
                    ? 'bg-green-500/10 border-green-500/30'
                    : analysis.summary?.avg_confidence >= 40
                      ? 'bg-yellow-500/10 border-yellow-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-text-primary">Overall Assessment</span>
                    {analysis.summary?.avg_confidence && (
                      <span className={`text-lg font-bold ${
                        analysis.summary.avg_confidence >= 60 ? 'text-green-400' :
                        analysis.summary.avg_confidence >= 40 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {analysis.summary.avg_confidence}% Confidence
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-text-secondary">{analysis.summary?.recommendation}</p>
                  <p className="text-xs text-text-secondary mt-1">
                    {analysis.summary?.analyzed_legs}/{analysis.summary?.total_legs} legs analyzed
                    {analysis.summary?.agreements > 0 && ` | ${analysis.summary.agreements} agree`}
                    {analysis.summary?.disagreements > 0 && ` | ${analysis.summary.disagreements} disagree`}
                  </p>
                </div>

                {/* Leg Analysis */}
                {analysis.analysis?.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-text-secondary">Leg Breakdown</h4>
                    {analysis.analysis.map((leg, idx) => (
                      <div
                        key={leg.leg_id || idx}
                        className="bg-bg-card rounded-lg border border-border p-3"
                      >
                        {/* Leg Header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-text-primary">
                              {leg.player_name || 'Game Bet'}
                            </span>
                            {leg.agrees_with_prediction !== null && leg.agrees_with_prediction !== undefined && (
                              leg.agrees_with_prediction ? (
                                <CheckCircleIcon className="w-4 h-4 text-green-400" />
                              ) : (
                                <XCircleIcon className="w-4 h-4 text-red-400" />
                              )
                            )}
                          </div>
                          {leg.confidence !== null && leg.confidence !== undefined && (
                            <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                              leg.confidence >= 65
                                ? 'bg-green-500/20 text-green-400'
                                : leg.confidence >= 45
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : 'bg-red-500/20 text-red-400'
                            }`}>
                              {leg.confidence}%
                            </span>
                          )}
                        </div>

                        {/* Bet Details */}
                        <div className="text-xs text-text-secondary mb-2">
                          {leg.bet_category && <span className="capitalize">{leg.bet_category}</span>}
                          {leg.selection && <span className="uppercase"> {leg.selection.replace('_inclusive', '+')}</span>}
                          {leg.line_value && <span> {leg.line_value}</span>}
                        </div>

                        {/* ML Prediction Details */}
                        {leg.has_prediction ? (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            {leg.ml_prediction !== null && (
                              <div className="bg-bg-primary p-2 rounded">
                                <span className="text-text-secondary block">ML Prediction</span>
                                <span className="text-accent-blue font-bold">{leg.ml_prediction}</span>
                              </div>
                            )}
                            {leg.sb_line !== null && (
                              <div className="bg-bg-primary p-2 rounded">
                                <span className="text-text-secondary block">Book Line</span>
                                <span className="text-text-primary font-bold">{leg.sb_line}</span>
                              </div>
                            )}
                            {leg.edge !== null && leg.edge !== undefined && (
                              <div className="bg-bg-primary p-2 rounded">
                                <span className="text-text-secondary block">Edge</span>
                                <span className={`font-bold ${leg.edge > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {leg.edge > 0 ? '+' : ''}{leg.edge}
                                </span>
                              </div>
                            )}
                            {leg.season_avg !== null && (
                              <div className="bg-bg-primary p-2 rounded">
                                <span className="text-text-secondary block">Season Avg</span>
                                <span className="text-text-primary font-bold">{leg.season_avg}</span>
                              </div>
                            )}
                            {leg.streak_hit_rate !== null && (
                              <div className="bg-bg-primary p-2 rounded">
                                <span className="text-text-secondary block">Streak Hit Rate</span>
                                <span className="text-purple-400 font-bold">{leg.streak_hit_rate}%</span>
                              </div>
                            )}
                            {leg.game_id && (
                              <div className="bg-bg-primary p-2 rounded">
                                <span className="text-text-secondary block">Game ID</span>
                                <span className="text-text-primary font-medium">{leg.game_id}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-text-secondary/70 italic">
                            {leg.message || 'No prediction data available'}
                          </p>
                        )}

                        {/* Analysis Note */}
                        {leg.analysis_note && (
                          <p className="mt-2 text-xs text-text-secondary bg-bg-primary p-2 rounded">
                            {leg.analysis_note}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 text-center text-text-secondary">
                Unable to load analysis
              </div>
            )}
          </div>

          {/* Bet Slip Image */}
          {imageUrl && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-bg-card p-3 border-b border-border">
                <h3 className="font-semibold text-text-primary">Original Bet Slip</h3>
              </div>
              <div className="p-4 flex justify-center">
                <img
                  src={imageUrl}
                  alt="Bet slip"
                  className="max-w-full max-h-96 object-contain rounded-lg"
                />
              </div>
            </div>
          )}

          {/* Notes */}
          {betslip.notes && (
            <div className="bg-bg-card p-3 rounded-lg">
              <p className="text-xs text-text-secondary mb-1">Notes</p>
              <p className="text-sm text-text-primary">{betslip.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex-shrink-0">
          <button
            onClick={handleClose}
            className="w-full py-2 px-4 rounded-lg font-medium bg-bg-card hover:bg-bg-card-hover text-text-primary transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
